import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { filter, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ELoadStatus } from '../../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../../utilities/http-utilities.service';
import { IWidgetRequest } from '../../widget-requests/widget-requests.interface';
import {
  ITableViewMapping,
  ITableViewMappingAddPayload,
  ITableViewMappingEditPayload,
} from '../table-view-widget-mappings.interface';

export interface ITableViewWidgetMappingAddEditDialogState {
  widgetRequests: IWidgetRequest[];
  widgetRequestsLoadStatus: ELoadStatus;
  createUpdateStatus: ELoadStatus;
}

@Injectable()
export class TableViewWidgetMappingAddEditDialogStore extends ComponentStore<ITableViewWidgetMappingAddEditDialogState> {
  private readonly http = inject(HttpClient);
  private readonly httpUtilities = inject(HttpUtilitiesService);

  public readonly widgetRequests = this.selectSignal(({ widgetRequests }) => widgetRequests);

  public readonly afterCreateUpdateSuccess$ = this.select(({ createUpdateStatus }) => createUpdateStatus).pipe(
    filter((status) => status === ELoadStatus.success),
  );

  public readonly loadWidgetRequests = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ widgetRequestsLoadStatus: ELoadStatus.loading });

        return this.getWidgetRequests(httpParams).pipe(
          tapResponse(
            (response) =>
              this.patchState({ widgetRequests: response.data, widgetRequestsLoadStatus: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly createOne = this.effect((trigger$: Observable<ITableViewMappingAddPayload>) =>
    trigger$.pipe(
      switchMap((item) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ createUpdateStatus: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error creating data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<ITableViewMappingEditPayload>) =>
    trigger$.pipe(
      switchMap((item: ITableViewMappingEditPayload) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ createUpdateStatus: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error updating data', error),
          ),
        );
      }),
    ),
  );

  constructor() {
    super({
      createUpdateStatus: ELoadStatus.initial,
      widgetRequests: [],
      widgetRequestsLoadStatus: ELoadStatus.initial,
    });
  }

  private getWidgetRequests(httpParams: HttpParams): Observable<IGetManyResponse<IWidgetRequest>> {
    return this.http.get<IGetManyResponse<IWidgetRequest>>('widget-requests', { params: httpParams });
  }

  private createItem(body: ITableViewMappingAddPayload): Observable<IBaseResponse<ITableViewMapping>> {
    return this.http.post<IBaseResponse<ITableViewMapping>>('table-view-widget-mappings', body);
  }

  private updateItem({ id, dto }: ITableViewMappingEditPayload): Observable<IBaseResponse<ITableViewMapping>> {
    return this.http.patch<IBaseResponse<ITableViewMapping>>(`table-view-widget-mappings/${id}`, dto);
  }
}
